import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Component({
  selector: 'app-dark-mode-toggle',
  template: `
    <button nxButton="secondary" nxSize="small" (click)="onToggle()"
            aria-label="Toggle dark mode" [attr.aria-pressed]="darkMode$ | async"
            class="dark-mode-button">
      <img src='../../../assets/icons/contrast.svg'
           [alt]="(darkMode$ | async) ? 'Dark mode enabled' : 'Dark mode disabled'"
          aria-hidden="true">
    </button>
  `,
  styleUrls: ['./dark-mode.component.scss'],
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- kept NgModule-based (DarkModeModule); converting to standalone is out of scope for this change
  standalone: false,
})

export class DarkModeComponent implements OnInit, OnDestroy {
  private readonly darkModeClass = 'dark-mode';
  private readonly lightModeClass = 'light-mode';
  private readonly preloadingClass = 'dark-mode-preloading';
  private readonly storageKey = 'dark-mode';
  private readonly prefersDarkQuery = '(prefers-color-scheme: dark)';

  private readonly darkModeSubject = new BehaviorSubject<boolean>(false);
  private mediaQuery?: MediaQueryList;
  private mediaQueryListener?: (e: MediaQueryListEvent) => void;

  darkMode$: Observable<boolean> = this.darkModeSubject.asObservable();

  constructor(@Inject(DOCUMENT) private readonly document: Document) { }

  ngOnInit(): void {
    this.initializeDarkMode();
    this.setupMediaQueryListener();
  }

  ngOnDestroy(): void {
    this.removeMediaQueryListener();
  }

  onToggle(): void {
    const newDarkModeValue = !this.darkModeSubject.value;
    this.setDarkMode(newDarkModeValue);
  }

  private initializeDarkMode(): void {
    let initialDarkModeValue = false;

    const darkModeFromStorage = localStorage.getItem(this.storageKey);

    if (darkModeFromStorage) {
      try {
        initialDarkModeValue = JSON.parse(darkModeFromStorage);
      } catch (err) {
        console.warn('Error initializing dark mode from localStorage');
        console.warn(err);
        initialDarkModeValue = this.getSystemPreference();
      }
    } else {
      initialDarkModeValue = this.getSystemPreference();
    }

    this.setDarkMode(initialDarkModeValue, false);
    setTimeout(() => {
      this.document.body.classList.remove(this.preloadingClass);
    }, 100);
  }

  private getSystemPreference(): boolean {
    return window.matchMedia(this.prefersDarkQuery).matches;
  }

  private setupMediaQueryListener(): void {
    this.mediaQuery = window.matchMedia(this.prefersDarkQuery);

    this.mediaQueryListener = (e: MediaQueryListEvent) => {
      const darkModeFromStorage = localStorage.getItem(this.storageKey);
      if (!darkModeFromStorage) {
        this.setDarkMode(e.matches, false);
      }
    };

    this.mediaQuery.addEventListener('change', this.mediaQueryListener);
  }

  private removeMediaQueryListener(): void {
    if (this.mediaQuery && this.mediaQueryListener) {
      this.mediaQuery.removeEventListener('change', this.mediaQueryListener);
    }
  }

  private setDarkMode(isDarkMode: boolean, saveToStorage = true): void {
    this.darkModeSubject.next(isDarkMode);
    this.updateBodyClasses(isDarkMode);

    if (saveToStorage) {
      this.saveToLocalStorage(isDarkMode);
    }
  }

  private updateBodyClasses(isDarkMode: boolean): void {
    const body = this.document.body;

    if (isDarkMode) {
      body.classList.remove(this.lightModeClass);
      body.classList.add(this.darkModeClass);
    } else {
      body.classList.remove(this.darkModeClass);
      body.classList.add(this.lightModeClass);
    }
  }

  private saveToLocalStorage(isDarkMode: boolean): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(isDarkMode));
    } catch (err) {
      console.warn('Error saving dark mode preference to localStorage');
      console.warn(err);
    }
  }
}
